import type { CSSProperties } from 'react'
import {
  type PartnerLogoMarkProps,
  type PartnersMarqueeProps,
} from '../constants/home'
import { usePartnerMarquee } from '../hooks/usePartnerMarquee'
import '../styles/home-page.scss'

function PartnerLogoMark({
  logo,
  decorative = false,
}: PartnerLogoMarkProps) {
  const image = (
    <img
      src={logo.imageUrl}
      alt={decorative ? '' : logo.title}
      className="partners__logo"
    />
  )

  if (!logo.linkUrl) return image

  return (
    <a
      href={logo.linkUrl}
      className="partners__link"
      target="_blank"
      rel="noreferrer noopener"
      tabIndex={decorative ? -1 : undefined}
      aria-hidden={decorative || undefined}
    >
      {image}
    </a>
  )
}

export function PartnersMarquee({ title, logos }: PartnersMarqueeProps) {
  const { sequence, durationSec, viewportRef, measureRef } = usePartnerMarquee(logos)

  return (
    <section className="section partners" aria-label={title}>
      <div className="container">
        <div className="section__head">
          <h2>{title}</h2>
        </div>

        {/* Hidden probe row — measures real logo spacing for copy count. */}
        <div className="partners__measure" aria-hidden="true">
          <div className="partners__group" ref={measureRef}>
            {logos.map((logo) => (
              <div key={`measure-${logo.id}`} className="partners__item">
                <PartnerLogoMark logo={logo} decorative />
              </div>
            ))}
          </div>
        </div>

        <div
          className="partners__viewport"
          ref={viewportRef}
          style={
            {
              '--partners-duration': `${durationSec}s`,
            } as CSSProperties
          }
        >
          <div className="partners__rail">
            <div className="partners__group" role="list">
              {sequence.map((logo, index) => (
                <div key={`${logo.id}-${index}`} className="partners__item" role="listitem">
                  <PartnerLogoMark logo={logo} />
                </div>
              ))}
            </div>
            <div className="partners__group" aria-hidden="true">
              {sequence.map((logo, index) => (
                <div key={`${logo.id}-dup-${index}`} className="partners__item">
                  <PartnerLogoMark logo={logo} decorative />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
